/**
 * ANATOMY Adaptive Free-Text Quiz — CLIENT BANK
 * ------------------------------------------------------------------
 * window.AnatomyQuizBank — the single client-side authority for:
 *   - the complete diagnostic QUESTION BANK (NODES + MANIFEST)
 *   - the canonical SIGNAL_TAXONOMY (mirrored by the worker strict tool;
 *     a parity hash guards drift — keep both sides identical)
 *   - the running-profile state machine: initProfile / mergeSignal /
 *     applyChip / scoreProfile / selectNext / estimatedTotal
 *   - buildPreliminary() + the qualitative preliminary copy bank
 *
 * Design rules baked in:
 *   - Pure client JS, no build step, no dependencies.
 *   - The CLIENT is the sole authority for all DERIVED numbers
 *     (severity_score, severity, loads, confidence). Haiku returns
 *     categorical patches ONLY — never numbers. scoreProfile() derives
 *     severity_score deterministically from `signals`, so the chip path
 *     and the free-text path score identically and idempotently.
 *   - Copy is honest + on-brand: sentence case, em-dashes (—, never --),
 *     product-neutral (no ingredient/molecule names in questions), no
 *     "proven"/"clinically proven", no percentages, no consumer-panel
 *     ("% saw") claims. No strength figure is used here; lab references
 *     belong to the streamed gpt-5-mini assessment, never here.
 *   - Default English copy ships inline so the bank is standalone-usable
 *     and testable; the localized theme copy (QuizConfig.bank) overlays
 *     onto it by key via applyLocale() at boot, preserving i18n.
 */
(function () {
  'use strict';

  var isKnown = function (v) { return v != null && v !== 'unknown'; };

  // ───────────────────────────────────────────────────────────────
  // SIGNAL_TAXONOMY (canonical). Flat dotted-path → allowed value set.
  // The worker's classify_turn strict-tool enums and this object MUST
  // stay byte-identical (parity test in the build's §7).
  // ───────────────────────────────────────────────────────────────
  var SIGNAL_TAXONOMY = {
    'frustration_theme':         ['breakage', 'thinning_shedding', 'wont_hold', 'dryness', 'prevention'],
    'goal':                      ['strength', 'length_retention', 'smooth', 'bounce', 'color_vibrancy', 'softness'],
    'chemical.process':          ['virgin', 'color_only', 'bleach_partial', 'bleach_global', 'relaxer_perm_keratin', 'henna_other', 'unknown'],
    'chemical.overlap':          ['roots_only', 'frequent', 'global', 'event', 'unknown'],
    'chemical.frequency':        ['rare', 'regular', 'frequent', 'unknown'],
    'chemical.recency':          ['recent', 'months', 'growing_out', 'unknown'],
    'chemical.stacking':         ['single', 'two_services', 'multiple', 'unknown'],
    'thermal.frequency':         ['never', 'weekly', 'most_days', 'daily', 'unknown'],
    'thermal.protectant':        ['always', 'sometimes', 'never', 'unknown'],
    'thermal.temp':              ['low', 'medium', 'high', 'unknown'],
    'mechanical.friction':       ['low', 'moderate', 'high', 'unknown'],
    'mechanical.context':        ['wet_brush', 'tension', 'extensions', 'ends', 'environment', 'none', 'unknown'],
    'hydration.porosity':        ['low', 'balanced', 'high', 'unknown'],
    'hydration.balance':         ['dry_all', 'oily_root_dry_ends', 'balanced', 'oily', 'unknown'],
    'hydration.product_response':['absorbs_lasting', 'temporary_coat', 'weighs_down', 'nothing_helps', 'unknown'],
    'bond.elasticity':           ['springs_back', 'stretches_stays', 'snaps_quick', 'unsure'],
    'severity.cuticle':          ['smooth', 'rough_ends', 'rough_all', 'mushy', 'unknown'],
    'severity.breakage':         ['minimal', 'ends', 'midshaft', 'widespread', 'unknown'],
    'loss_type':                 ['breakage', 'shedding_root', 'both', 'unsure', 'unknown'],
    'routine.prior_treatment':   ['tried_disappointed', 'tried_plateau', 'tried_liked', 'aware_untried', 'unaware', 'unknown'],
    'routine.aftercare':         ['high', 'moderate', 'low', 'committed', 'unknown']
  };

  // Fast lookup: dotted-path → Set(allowed values)
  var TAXONOMY_SET = {};
  Object.keys(SIGNAL_TAXONOMY).forEach(function (p) {
    var set = {};
    SIGNAL_TAXONOMY[p].forEach(function (v) { set[v] = true; });
    TAXONOMY_SET[p] = set;
  });

  // Top-level scalar signals (live on the profile root or signals.loss_type)
  var TOP_SCALARS = { frustration_theme: true, goal: true, loss_type: true };

  // Categorical dimensions (objects under profile.signals)
  var DIM_KEYS = {
    chemical:   ['process', 'overlap', 'frequency', 'recency', 'stacking'],
    thermal:    ['frequency', 'protectant', 'temp'],
    mechanical: ['friction', 'context'],
    hydration:  ['porosity', 'balance', 'product_response'],
    bond:       ['elasticity'],
    severity:   ['cuticle', 'breakage'],
    routine:    ['prior_treatment', 'aftercare']
  };

  // Primary keys that mark a dimension "covered" (derived keys excluded).
  var PRIMARY_KEYS = {
    chemical:   ['process', 'overlap', 'frequency', 'recency', 'stacking'],
    thermal:    ['frequency', 'protectant', 'temp'],
    mechanical: ['friction', 'context'],
    hydration:  ['porosity', 'balance', 'product_response'],
    bond:       ['elasticity'],
    severity:   ['cuticle', 'breakage'],
    routine:    ['prior_treatment', 'aftercare']
  };

  // ───────────────────────────────────────────────────────────────
  // SEVERITY WEIGHTS (signal → integer). The worker mirrors these when
  // it recomputes severity from signals at assessment time. Designed so
  // a typical one-select-per-dimension path reproduces the chip-sum, and
  // so chip and free-text paths land in the same severity band.
  // ───────────────────────────────────────────────────────────────
  var SIGNAL_WEIGHTS = {
    frustration_theme: { breakage: 2, dryness: 2, wont_hold: 2, thinning_shedding: 2, prevention: 0 },
    chemical: {
      process:   { virgin: 0, henna_other: 1, color_only: 1, relaxer_perm_keratin: 2, bleach_partial: 2, bleach_global: 3 },
      overlap:   { roots_only: 1, frequent: 2, event: 3, global: 3 },
      frequency: { rare: 0, regular: 1, frequent: 2 },
      recency:   { growing_out: 1, months: 1, recent: 2 },
      stacking:  { single: 0, two_services: 1, multiple: 2 }
    },
    thermal:    { frequency: { never: 0, weekly: 1, most_days: 2, daily: 3 } },
    mechanical: { friction:  { low: 0, moderate: 1, high: 2 } },
    hydration:  {
      porosity:         { low: 1, balanced: 0, high: 2 },
      product_response: { absorbs_lasting: 0, temporary_coat: 1, weighs_down: 1, nothing_helps: 2 }
    },
    bond:     { elasticity: { springs_back: 0, stretches_stays: 3, snaps_quick: 3, unsure: 1 } },
    severity: { cuticle:    { smooth: 0, rough_ends: 1, rough_all: 2, mushy: 3 } },
    loss_type: { breakage: 1, both: 1, shedding_root: 0, unsure: 0 }
  };

  var LOAD_ORD = { none: 0, low: 1, moderate: 2, high: 3, severe: 4, unknown: 0 };

  // ───────────────────────────────────────────────────────────────
  // CONFIG — mutable so the section settings (min_questions / max_questions
  // / confidence_target) can override at boot. selectNext reads these.
  // ───────────────────────────────────────────────────────────────
  var CONFIG = {
    minQuestions: 4,
    maxQuestions: 6,
    confidenceTarget: 0.78
  };

  // ───────────────────────────────────────────────────────────────
  // THE QUESTION BANK — 14 nodes across 6 diagnostic dimensions.
  // Gating means a user sees 4–6. Anchor (Q1) is static in Liquid but
  // defined here for the structured fast path + free-text mapping.
  // Each chip: { value, label, labelKey, signal (categorical patch),
  //             severity (integer, for the spec onChip += / reference) }.
  // ───────────────────────────────────────────────────────────────
  var NODES = {

    /* DIM: context — anchor (Q1). Values LOCKED to bridgeTexts keys. */
    anchor: {
      id: 'anchor', dimension: 'context', gate: 'always', allowFreeText: true,
      promptKey: 'bank_anchor_prompt',
      prompt: "What's going on with your hair right now?",
      chips: [
        { value: 'damaged_brittle', labelKey: 'bank_anchor_damaged',    label: 'Dry, rough, or brittle',                 signal: { frustration_theme: 'dryness' },   severity: 2 },
        { value: 'losing_strength', labelKey: 'bank_anchor_losing',     label: "Weak — it's breaking",           signal: { frustration_theme: 'breakage' },  severity: 2 },
        { value: 'no_elasticity',   labelKey: 'bank_anchor_elasticity', label: 'Limp, with no bounce',     signal: { frustration_theme: 'wont_hold' }, severity: 2 },
        { value: 'prevention',      labelKey: 'bank_anchor_prevention', label: 'Healthy — protecting it',             signal: { frustration_theme: 'prevention' },severity: 0 }
      ]
    },

    /* DIM: context — goal (optional, personalises the result). */
    ctx_goal: {
      id: 'ctx_goal', dimension: 'context', gate: 'always', allowFreeText: true,
      promptKey: 'bank_ctx_goal_prompt',
      prompt: 'When your hair is at its best, what does that look like to you?',
      chips: [
        { value: 'goal_strength',  labelKey: 'bank_ctx_goal_strength', label: 'Strong, resilient strands', signal: { goal: 'strength' },         severity: 0 },
        { value: 'goal_length',    labelKey: 'bank_ctx_goal_length',   label: 'Length without breakage',    signal: { goal: 'length_retention' }, severity: 0 },
        { value: 'goal_smooth',    labelKey: 'bank_ctx_goal_smooth',   label: 'Smooth and sleek',        signal: { goal: 'smooth' },           severity: 0 },
        { value: 'goal_bounce',    labelKey: 'bank_ctx_goal_bounce',   label: 'Bounce and movement',       signal: { goal: 'bounce' },           severity: 0 },
        { value: 'goal_color',     labelKey: 'bank_ctx_goal_color',    label: 'Vibrant, lasting colour',        signal: { goal: 'color_vibrancy' },   severity: 0 }
      ]
    },

    /* DIM: chemical — always; gates all chemical follow-ups. */
    chemical: {
      id: 'chemical', dimension: 'chemical', gate: 'always', allowFreeText: true,
      promptKey: 'bank_chemical_prompt',
      prompt: 'How is your hair coloured or chemically treated?',
      chips: [
        { value: 'virgin',               labelKey: 'bank_chemical_virgin',  label: 'Natural — never coloured',     signal: { chemical: { process: 'virgin' } },               severity: 0 },
        { value: 'color_only',           labelKey: 'bank_chemical_color',   label: 'Coloured, never bleached',     signal: { chemical: { process: 'color_only' } },           severity: 1 },
        { value: 'bleach_partial',       labelKey: 'bank_chemical_partial', label: 'Highlights or partial bleach',         signal: { chemical: { process: 'bleach_partial' } },       severity: 2 },
        { value: 'bleach_global',        labelKey: 'bank_chemical_global',  label: 'Bleached or lightened all over',signal: { chemical: { process: 'bleach_global' } },        severity: 3 },
        { value: 'relaxer_perm_keratin', labelKey: 'bank_chemical_relaxer', label: 'Relaxed, permed, or keratin',      signal: { chemical: { process: 'relaxer_perm_keratin' } }, severity: 2 }
      ]
    },

    /* DIM: chemical — bleach overlap (gate: process ∈ bleach_*). */
    chem_bleach_overlap: {
      id: 'chem_bleach_overlap', dimension: 'chemical', allowFreeText: true,
      gate: function (s) { return s.chemical && (s.chemical.process === 'bleach_partial' || s.chemical.process === 'bleach_global'); },
      promptKey: 'bank_chem_overlap_prompt',
      prompt: 'When you lighten, how often does the bleach land on hair that was already lightened?',
      chips: [
        { value: 'overlap_roots',    labelKey: 'bank_chem_overlap_roots',    label: 'Mostly roots — little overlap',          signal: { chemical: { overlap: 'roots_only' } },                  severity: 1 },
        { value: 'overlap_frequent', labelKey: 'bank_chem_overlap_frequent', label: 'Overlaps the lengths often',    signal: { chemical: { overlap: 'frequent' } },                    severity: 2 },
        { value: 'overlap_global',   labelKey: 'bank_chem_overlap_global',   label: 'The whole length, every time',signal: { chemical: { overlap: 'global' } },                      severity: 3 },
        { value: 'overlap_event',    labelKey: 'bank_chem_overlap_event',    label: 'One big session recently',     signal: { chemical: { overlap: 'event', recency: 'recent' } },    severity: 3 }
      ]
    },

    /* DIM: chemical — colour frequency (gate: process == color_only). */
    chem_color_freq: {
      id: 'chem_color_freq', dimension: 'chemical', allowFreeText: true,
      gate: function (s) { return s.chemical && s.chemical.process === 'color_only'; },
      promptKey: 'bank_chem_colorfreq_prompt',
      prompt: 'How often do you colour or refresh your shade?',
      chips: [
        { value: 'color_rare',     labelKey: 'bank_chem_colorfreq_rare',    label: 'A few times a year',                       signal: { chemical: { frequency: 'rare' } },     severity: 0 },
        { value: 'color_regular',  labelKey: 'bank_chem_colorfreq_regular', label: 'Every month or two',                       signal: { chemical: { frequency: 'regular' } },  severity: 1 },
        { value: 'color_frequent', labelKey: 'bank_chem_colorfreq_often',   label: 'Often — frequent touch-ups', signal: { chemical: { frequency: 'frequent' } }, severity: 2 },
        { value: 'color_box',      labelKey: 'bank_chem_colorfreq_box',     label: 'Mostly box dye at home',                   signal: { chemical: { frequency: 'frequent' } }, severity: 2 }
      ]
    },

    /* DIM: chemical — relaxer recency (gate: process == relaxer_perm_keratin). */
    chem_relaxer_recency: {
      id: 'chem_relaxer_recency', dimension: 'chemical', allowFreeText: true,
      gate: function (s) { return s.chemical && s.chemical.process === 'relaxer_perm_keratin'; },
      promptKey: 'bank_chem_relaxer_prompt',
      prompt: 'How recent is your last relaxer, perm, or keratin treatment?',
      chips: [
        { value: 'relaxer_recent',  labelKey: 'bank_chem_relaxer_recent',  label: 'Within the last few months',           signal: { chemical: { recency: 'recent' } },                    severity: 2 },
        { value: 'relaxer_months',  labelKey: 'bank_chem_relaxer_months',  label: 'Several months ago',                         signal: { chemical: { recency: 'months' } },                    severity: 1 },
        { value: 'relaxer_growing', labelKey: 'bank_chem_relaxer_growing', label: 'A while back — growing it out',           signal: { chemical: { recency: 'growing_out' } },               severity: 1 },
        { value: 'relaxer_overlap', labelKey: 'bank_chem_relaxer_overlap', label: 'I overlap onto treated hair',signal: { chemical: { recency: 'recent', overlap: 'global' } }, severity: 3 }
      ]
    },

    /* DIM: thermal — always; gates thermal follow-ups. */
    thermal: {
      id: 'thermal', dimension: 'thermal', gate: 'always', allowFreeText: true,
      promptKey: 'bank_thermal_prompt',
      prompt: 'How often do you use hot tools — flat iron, curling iron, or a hot blow-dry?',
      chips: [
        { value: 'thermal_rarely',    labelKey: 'bank_thermal_rarely',  label: 'Rarely — I air-dry',            signal: { thermal: { frequency: 'never' } },                                  severity: 0 },
        { value: 'thermal_weekly',    labelKey: 'bank_thermal_weekly',  label: 'A few times a week',                   signal: { thermal: { frequency: 'weekly' } },                                 severity: 1 },
        { value: 'thermal_most_days', labelKey: 'bank_thermal_most',    label: 'Most days, with protectant', signal: { thermal: { frequency: 'most_days', protectant: 'always' } },         severity: 2 },
        { value: 'thermal_daily',     labelKey: 'bank_thermal_daily',   label: 'Daily — high heat, no protectant',      signal: { thermal: { frequency: 'daily', protectant: 'never', temp: 'high' } },severity: 3 }
      ]
    },

    /* DIM: mechanical — detangling / friction. */
    mech_detangling: {
      id: 'mech_detangling', dimension: 'mechanical', gate: 'always', allowFreeText: true,
      promptKey: 'bank_mech_prompt',
      prompt: 'How do you usually detangle — and is your hair wet or dry when you brush?',
      chips: [
        { value: 'mech_gentle',  labelKey: 'bank_mech_gentle',  label: 'Gently — wide-tooth comb',        signal: { mechanical: { friction: 'low' } },                       severity: 0 },
        { value: 'mech_wet',     labelKey: 'bank_mech_wet',     label: 'I brush it wet',            signal: { mechanical: { friction: 'high', context: 'wet_brush' } },severity: 2 },
        { value: 'mech_tension', labelKey: 'bank_mech_tension', label: 'Tight ponytails or braids', signal: { mechanical: { friction: 'high', context: 'tension' } },  severity: 2 },
        { value: 'mech_snaps',   labelKey: 'bank_mech_snaps',   label: 'It tangles and snaps',          signal: { mechanical: { friction: 'high', context: 'ends' } },     severity: 2 }
      ]
    },

    /* DIM: hydration_porosity — the cleanest porosity read. */
    hp_porosity: {
      id: 'hp_porosity', dimension: 'hydration', gate: 'always', allowFreeText: true,
      promptKey: 'bank_hp_porosity_prompt',
      prompt: 'When your hair gets wet, does water bead and sit on top — or soak straight in?',
      chips: [
        { value: 'poro_low',      labelKey: 'bank_hp_porosity_low',      label: 'Beads up — slow to wet',  signal: { hydration: { porosity: 'low' } },      severity: 1 },
        { value: 'poro_balanced', labelKey: 'bank_hp_porosity_balanced', label: 'Wets and dries evenly',   signal: { hydration: { porosity: 'balanced' } }, severity: 0 },
        { value: 'poro_high',     labelKey: 'bank_hp_porosity_high',     label: 'Soaks up instantly', signal: { hydration: { porosity: 'high' } },  severity: 2 },
        { value: 'poro_unknown',  labelKey: 'bank_hp_porosity_unknown',  label: "I can't tell",             signal: { hydration: { porosity: 'unknown' } },  severity: 0 }
      ]
    },

    /* DIM: hydration_porosity — product response (why previous underperformed). */
    hp_product_response: {
      id: 'hp_product_response', dimension: 'hydration', gate: 'always', allowFreeText: true,
      promptKey: 'bank_hp_response_prompt',
      prompt: 'When you use masks or conditioners, how does your hair actually respond?',
      chips: [
        { value: 'pr_absorbs',   labelKey: 'bank_hp_response_absorbs',   label: 'Drinks it in — lasts for days', signal: { hydration: { product_response: 'absorbs_lasting' } }, severity: 0 },
        { value: 'pr_temporary', labelKey: 'bank_hp_response_temporary', label: 'Great briefly, then rough again',     signal: { hydration: { product_response: 'temporary_coat' } },  severity: 1 },
        { value: 'pr_weighs',    labelKey: 'bank_hp_response_weighs',    label: 'Heavy — it weighs down',           signal: { hydration: { product_response: 'weighs_down' } },     severity: 1 },
        { value: 'pr_nothing',   labelKey: 'bank_hp_response_nothing',   label: 'Nothing really changes it',           signal: { hydration: { product_response: 'nothing_helps' } },   severity: 2 }
      ]
    },

    /* DIM: severity — the elasticity readout. Asked once, never twice. */
    sev_elasticity: {
      id: 'sev_elasticity', dimension: 'bond', gate: 'always', allowFreeText: true,
      promptKey: 'bank_sev_elasticity_prompt',
      prompt: 'Take one shed, damp strand and gently stretch it — what happens?',
      microKey: 'bank_sev_elasticity_micro',
      micro: 'This helps distinguish reduced elasticity from surface roughness; it is not a laboratory measurement.',
      chips: [
        { value: 'elastic_springs',   labelKey: 'bank_sev_elasticity_springs',   label: 'Stretches, then springs back',     signal: { bond: { elasticity: 'springs_back' } },   severity: 0 },
        { value: 'elastic_stretches', labelKey: 'bank_sev_elasticity_stretches', label: 'Stretches and stays limp',   signal: { bond: { elasticity: 'stretches_stays' } },severity: 3 },
        { value: 'elastic_snaps',     labelKey: 'bank_sev_elasticity_snaps',     label: 'Barely stretches — it snaps',signal: { bond: { elasticity: 'snaps_quick' } },    severity: 3 },
        { value: 'elastic_unsure',    labelKey: 'bank_sev_elasticity_unsure',    label: "I'm not sure",                                 signal: { bond: { elasticity: 'unsure' } },         severity: 1 }
      ]
    },

    /* DIM: severity — honesty guard. Forced when shedding/thinning is suspected. */
    sev_loss_type: {
      id: 'sev_loss_type', dimension: 'loss_type', allowFreeText: true,
      gate: function (s, p) { return !!(p && p.flags && p.flags.shedding_suspected); },
      promptKey: 'bank_sev_loss_prompt',
      prompt: 'When you lose hairs, do they look broken — or do they have a tiny white bulb at the end?',
      chips: [
        { value: 'loss_breakage', labelKey: 'bank_sev_loss_breakage', label: 'Broken — no bulb', signal: { loss_type: 'breakage' },     severity: 1 },
        { value: 'loss_shedding', labelKey: 'bank_sev_loss_shedding', label: 'Whole hairs, tiny bulb',        signal: { loss_type: 'shedding_root' }, severity: 0 },
        { value: 'loss_both',     labelKey: 'bank_sev_loss_both',     label: 'A mix of both',                       signal: { loss_type: 'both' },         severity: 1 },
        { value: 'loss_unsure',   labelKey: 'bank_sev_loss_unsure',   label: "I can't tell",                        signal: { loss_type: 'unsure' },       severity: 0 }
      ]
    },

    /* DIM: severity — surface confirm (gate: elasticity == unsure). */
    sev_surface: {
      id: 'sev_surface', dimension: 'severity', allowFreeText: true,
      gate: function (s) { return s.bond && s.bond.elasticity === 'unsure'; },
      promptKey: 'bank_sev_surface_prompt',
      prompt: 'Run your fingers down a strand — how does the surface feel?',
      chips: [
        { value: 'surf_smooth',     labelKey: 'bank_sev_surface_smooth',    label: 'Smooth and shiny',                      signal: { severity: { cuticle: 'smooth' } },     severity: 0 },
        { value: 'surf_rough_ends', labelKey: 'bank_sev_surface_roughends', label: 'A little rough at the ends',       signal: { severity: { cuticle: 'rough_ends' } }, severity: 1 },
        { value: 'surf_rough_all',  labelKey: 'bank_sev_surface_roughall',  label: 'Rough all the way down',  signal: { severity: { cuticle: 'rough_all' } },  severity: 2 },
        { value: 'surf_mushy',      labelKey: 'bank_sev_surface_mushy',     label: 'Mushy when wet',          signal: { severity: { cuticle: 'mushy' } },      severity: 3 }
      ]
    },

    /* DIM: routine_history — closer A (why previous underperformed). */
    rt_prior_bondrepair: {
      id: 'rt_prior_bondrepair', dimension: 'routine', gate: 'always', allowFreeText: true,
      promptKey: 'bank_rt_prior_prompt',
      prompt: 'Have you tried bond-repair or strengthening treatments before?',
      chips: [
        { value: 'prior_disappointed', labelKey: 'bank_rt_prior_disappointed', label: "Tried them — didn't hold", signal: { routine: { prior_treatment: 'tried_disappointed' } }, severity: 0 },
        { value: 'prior_plateau',      labelKey: 'bank_rt_prior_plateau',      label: 'Helped, then plateaued', signal: { routine: { prior_treatment: 'tried_plateau' } },      severity: 0 },
        { value: 'prior_liked',        labelKey: 'bank_rt_prior_liked',        label: 'Tried them — liked them',             signal: { routine: { prior_treatment: 'tried_liked' } },        severity: 0 },
        { value: 'prior_aware',        labelKey: 'bank_rt_prior_aware',        label: 'Heard of them, not tried',        signal: { routine: { prior_treatment: 'aware_untried' } },      severity: 0 },
        { value: 'prior_unaware',      labelKey: 'bank_rt_prior_unaware',      label: "What's bond repair?",         signal: { routine: { prior_treatment: 'unaware' } },            severity: 0 }
      ]
    },

    /* DIM: routine_history — closer B (sets protocol entry depth). */
    rt_aftercare: {
      id: 'rt_aftercare', dimension: 'routine', gate: 'always', allowFreeText: true,
      promptKey: 'bank_rt_aftercare_prompt',
      prompt: 'How much aftercare does your hair realistically get — leave-ins, treatments, trims?',
      chips: [
        { value: 'after_high',      labelKey: 'bank_rt_aftercare_high',      label: "A lot — I'm consistent",         signal: { routine: { aftercare: 'high' } },      severity: 0 },
        { value: 'after_moderate',  labelKey: 'bank_rt_aftercare_moderate',  label: 'Some — when I remember',         signal: { routine: { aftercare: 'moderate' } },  severity: 0 },
        { value: 'after_low',       labelKey: 'bank_rt_aftercare_low',       label: 'Very little — low effort',signal: { routine: { aftercare: 'low' } },       severity: 0 },
        { value: 'after_committed', labelKey: 'bank_rt_aftercare_committed', label: 'Starting fresh — all in',signal: { routine: { aftercare: 'committed' } }, severity: 0 }
      ]
    }
  };

  // ───────────────────────────────────────────────────────────────
  // MANIFEST — lightweight bank descriptor sent to the worker so Haiku
  // can name the next informative question. [{id, dimension, resolves}]
  // ───────────────────────────────────────────────────────────────
  function collectPaths(sig, prefix, out) {
    Object.keys(sig).forEach(function (k) {
      var v = sig[k];
      var path = prefix ? prefix + '.' + k : k;
      if (v && typeof v === 'object') collectPaths(v, path, out);
      else if (out.indexOf(path) === -1) out.push(path);
    });
  }
  var MANIFEST = Object.keys(NODES).map(function (id) {
    var node = NODES[id];
    var resolves = [];
    (node.chips || []).forEach(function (c) { if (c.signal) collectPaths(c.signal, '', resolves); });
    return { id: id, dimension: node.dimension, resolves: resolves };
  });

  // dimensionOf(nodeId) → dimension string
  var DIMENSION_OF = {};
  Object.keys(NODES).forEach(function (id) { DIMENSION_OF[id] = NODES[id].dimension; });

  // ───────────────────────────────────────────────────────────────
  // PRELIMINARY COPY BANK — qualitative only. NEVER a number or "%";
  // lab references belong to the streamed assessment. No
  // ingredient/molecule names, no "proven"/"% saw". severity × dominant
  // mechanism, with a prevention/early branch that never fabricates
  // cortex damage.
  // ───────────────────────────────────────────────────────────────
  var PRELIMINARY_COPY = {
    prevention: {
      headline: 'Your answers point to a strong starting condition',
      summary: 'You reported few signs of current fibre damage. The useful focus is protecting structural integrity as colour, heat, and friction accumulate over time.',
      insight: 'A consistent protective routine can reduce avoidable structural stress before breakage or loss of elasticity becomes noticeable.',
      timeline: 'Keep the routine consistent and compare the fibre against your current baseline after one complete usage cycle.'
    },
    mild: {
      _base: {
        headline: 'Your answers point to early fibre wear',
        summary: 'The signs you reported appear limited, with the fibre still retaining much of its strength and elasticity. Consistency matters more than intensity at this stage.',
        insight: 'The pattern appears early enough to focus on reducing repeat exposure and supporting the existing structure.',
        timeline: 'Assess the result after one complete usage cycle before changing the protocol.'
      }
    },
    moderate: {
      _base: {
        headline: 'Your answers point to cumulative structural stress',
        summary: 'The combination you reported is consistent with reduced internal strength rather than a surface-feel concern alone. The most useful next step is a consistent reconstruction protocol.',
        insight: 'The pattern may extend beyond the cuticle into the structure that gives the fibre strength and elasticity.',
        timeline: 'Assess the result after one complete usage cycle before changing the protocol.'
      },
      chemical:   { insight: 'Colour or lightening is the strongest structural stressor in the pattern you reported.' },
      thermal:    { insight: 'Repeated heat is the strongest contributor in the pattern you reported.' },
      mechanical: { insight: 'Repeated friction and tension are the strongest contributors in the pattern you reported.' },
      hydration:  { insight: 'The roughness and porosity you reported may be amplifying the fibre\u2019s structural stress.' }
    },
    moderate_severe: {
      _base: {
        headline: 'Your answers point to reduced strength and elasticity',
        summary: 'The reported combination of roughness, reduced stretch, and breakage is consistent with structural wear through the processed lengths. A surface-only approach may not address the same mechanism.',
        insight: 'The clearest signal is the change in how the fibre behaves under tension, not surface feel alone.',
        timeline: 'Assess the result after one complete usage cycle before changing the protocol.'
      },
      chemical:   { insight: 'Repeated lightening, especially where bleach overlaps processed lengths, is the strongest contributor you reported.' },
      thermal:    { insight: 'Frequent high heat without protection is the strongest contributor you reported.' },
      mechanical: { insight: 'Persistent friction and tension are the strongest contributors you reported.' },
      hydration:  { insight: 'The porosity and roughness you reported may be amplifying the underlying structural wear.' }
    },
    severe: {
      _base: {
        headline: 'Your answers point to advanced cumulative fibre damage',
        summary: 'You reported pronounced changes in strength or elasticity, including strands that snap with little resistance or stretch without recovering. This pattern suggests structural wear beyond surface roughness alone.',
        insight: 'The reported loss of elasticity is the strongest signal that the reconstruction protocol should address internal fibre structure.',
        timeline: 'Assess the result after one complete usage cycle before changing the protocol.'
      },
      chemical:   { insight: 'Frequent overlapping lightening is the strongest structural stressor you reported.' },
      thermal:    { insight: 'Sustained high heat is the strongest structural stressor you reported.' },
      mechanical: { insight: 'Repeated mechanical stress is the strongest structural stressor you reported.' },
      hydration:  { insight: 'The pronounced porosity and roughness you reported may be compounding the structural wear.' }
    }
  };

  // ───────────────────────────────────────────────────────────────
  // PROFILE STATE
  // ───────────────────────────────────────────────────────────────
  function initProfile(sessionId) {
    var now = new Date().toISOString();
    return {
      schema_version: 2,
      session_id: sessionId || null,
      asked_ids: [],
      turn_count: 0,
      frustration_theme: null,
      goal: null,
      signals: {
        chemical:   { process: 'unknown', overlap: 'unknown', frequency: 'unknown', recency: 'unknown', stacking: 'unknown', load: 'unknown' },
        thermal:    { frequency: 'unknown', protectant: 'unknown', temp: 'unknown', load: 'unknown' },
        mechanical: { friction: 'unknown', context: 'unknown', load: 'unknown' },
        hydration:  { porosity: 'unknown', balance: 'unknown', product_response: 'unknown', coating_dependent: false },
        bond:       { elasticity: null, integrity: 'unknown' },
        severity:   { cuticle: 'unknown', breakage: 'unknown' },
        loss_type:  'unknown',
        routine:    { prior_treatment: 'unknown', aftercare: 'unknown' }
      },
      severity_score: 0,
      severity: 'mild',
      dominant_mechanism: null,
      secondary_mechanisms: [],
      covered_dimensions: [],
      confidence: 0,
      flags: {
        vague_last: false, contradiction: false, contradiction_note: null,
        off_topic_count: 0, degraded: false, shedding_suspected: false
      },
      narrative: { bridge_text: '', micro_insight: '' },
      next_hint: null,
      created_at: now,
      updated_at: now
    };
  }

  // taxonomyAllows(path, value)
  function taxonomyAllows(path, value) {
    var set = TAXONOMY_SET[path];
    return !!(set && set[value] === true);
  }

  // mergeSignal — deep-merge a categorical patch (chip OR Haiku) into the
  // running profile. Whitelists every value against SIGNAL_TAXONOMY
  // (defense-in-depth) and never overwrites a known value with
  // 'unknown'/null. Ignores derived keys (load/integrity/coating_dependent).
  function mergeSignal(profile, patch) {
    if (!profile || !patch || typeof patch !== 'object') return profile;
    if (!profile.signals) profile.signals = initProfile(profile.session_id).signals;
    var S = profile.signals;

    Object.keys(patch).forEach(function (key) {
      var val = patch[key];

      // Top-level scalars
      if (TOP_SCALARS[key]) {
        if (typeof val !== 'string') return;
        if (!isKnown(val) || !taxonomyAllows(key, val)) return;
        if (key === 'loss_type') S.loss_type = val;
        else profile[key] = val; // frustration_theme | goal
        return;
      }

      // Categorical dimensions
      if (DIM_KEYS[key] && val && typeof val === 'object') {
        if (!S[key]) S[key] = {};
        Object.keys(val).forEach(function (sub) {
          if (DIM_KEYS[key].indexOf(sub) === -1) return; // primary keys only
          var sv = val[sub];
          var path = key + '.' + sub;
          if (typeof sv !== 'string' || !isKnown(sv) || !taxonomyAllows(path, sv)) return;
          S[key][sub] = sv;
        });
      }
    });

    return profile;
  }

  // applyChip — higher-level helper: merge the chip's signal, record the
  // node as asked, re-score. (The client may instead call mergeSignal +
  // scoreProfile directly; both routes are equivalent.)
  function applyChip(profile, chip, nodeId) {
    if (!profile || !chip) return profile;
    mergeSignal(profile, chip.signal);
    var id = nodeId || (chip.nodeId);
    if (id && profile.asked_ids.indexOf(id) === -1) profile.asked_ids.push(id);
    scoreProfile(profile);
    return profile;
  }

  // ── derived: per-dimension loads ──
  function chemicalLoad(c) {
    if (!c || !isKnown(c.process) || c.process === 'virgin') {
      // virgin but other chemical noise → low at most
      if (c && (isKnown(c.overlap) || isKnown(c.frequency) || isKnown(c.recency))) return 'low';
      return 'none';
    }
    var base = { color_only: 'low', henna_other: 'low', relaxer_perm_keratin: 'moderate', bleach_partial: 'moderate', bleach_global: 'high' }[c.process] || 'low';
    var ord = LOAD_ORD[base];
    if (c.overlap === 'global') ord += 2;
    else if (c.overlap === 'frequent' || c.overlap === 'event') ord += 1;
    if (c.recency === 'recent' && c.process === 'relaxer_perm_keratin') ord += 1;
    if (c.frequency === 'frequent' && c.process === 'color_only') ord += 1;
    if (c.stacking === 'multiple') ord += 1;
    return ordToLoad(ord);
  }
  function thermalLoad(t) {
    if (!t || !isKnown(t.frequency) || t.frequency === 'never') return 'none';
    if (t.frequency === 'weekly') return 'low';
    if (t.frequency === 'most_days') return t.protectant === 'never' ? 'high' : 'moderate';
    if (t.frequency === 'daily') return 'high';
    return 'none';
  }
  function mechanicalLoad(m) {
    if (!m || !isKnown(m.friction)) return 'none';
    if (m.friction === 'high') return 'high';
    if (m.friction === 'moderate') return 'moderate';
    if (m.friction === 'low') return 'low';
    return 'none';
  }
  function hydrationLoad(h) {
    if (!h) return 'none';
    if (h.product_response === 'nothing_helps') return 'high';
    if (h.porosity === 'high') return 'moderate';
    if (h.porosity === 'low' || h.product_response === 'temporary_coat' || h.product_response === 'weighs_down') return 'low';
    return 'none';
  }
  function ordToLoad(ord) {
    if (ord >= 4) return 'severe';
    if (ord >= 3) return 'high';
    if (ord >= 2) return 'moderate';
    if (ord >= 1) return 'low';
    return 'none';
  }

  // severity_score derived from signals (single source of truth)
  function severityFromSignals(signals, frustrationTheme) {
    var s = 0;
    var W = SIGNAL_WEIGHTS;
    var add = function (table, val) { if (isKnown(val) && table && table[val] != null) s += table[val]; };

    add(W.frustration_theme, frustrationTheme);

    var c = signals.chemical || {};
    add(W.chemical.process, c.process);
    add(W.chemical.overlap, c.overlap);
    add(W.chemical.frequency, c.frequency);
    add(W.chemical.recency, c.recency);
    add(W.chemical.stacking, c.stacking);

    add(W.thermal.frequency, (signals.thermal || {}).frequency);
    add(W.mechanical.friction, (signals.mechanical || {}).friction);

    var h = signals.hydration || {};
    add(W.hydration.porosity, h.porosity);
    add(W.hydration.product_response, h.product_response);

    add(W.bond.elasticity, (signals.bond || {}).elasticity);
    add(W.severity.cuticle, (signals.severity || {}).cuticle);
    add(W.loss_type, signals.loss_type);

    // compounding: chemical AND thermal both heavy
    if (LOAD_ORD[chemicalLoad(c)] >= 3 && LOAD_ORD[thermalLoad(signals.thermal || {})] >= 3) s += 2;

    return s;
  }

  function severityBand(score) {
    if (score >= 9) return 'severe';
    if (score >= 6) return 'moderate_severe';
    if (score >= 3) return 'moderate';
    return 'mild';
  }

  var SEVERITY_ORD = { mild: 0, moderate: 1, moderate_severe: 2, severe: 3 };

  function coveredDimension(profile, dim) {
    var S = profile.signals || {};
    if (dim === 'context') return isKnown(profile.frustration_theme);
    if (dim === 'loss_type') return isKnown(S.loss_type);
    var keys = PRIMARY_KEYS[dim];
    if (!keys) return false;
    var d = S[dim] || {};
    return keys.some(function (k) { return isKnown(d[k]); });
  }

  // scoreProfile — recompute ALL derived fields deterministically from
  // signals. Idempotent + order-independent → chip and free-text paths
  // produce identical numbers. SETS severity_score (so any external
  // accumulation is normalized away).
  function scoreProfile(profile) {
    if (!profile) return profile;
    if (!profile.signals) profile.signals = initProfile(profile.session_id).signals;
    var S = profile.signals;

    // loads
    var loads = {
      chemical: chemicalLoad(S.chemical || {}),
      thermal: thermalLoad(S.thermal || {}),
      mechanical: mechanicalLoad(S.mechanical || {}),
      hydration: hydrationLoad(S.hydration || {})
    };
    if (S.chemical) S.chemical.load = loads.chemical;
    if (S.thermal) S.thermal.load = loads.thermal;
    if (S.mechanical) S.mechanical.load = loads.mechanical;

    // bond integrity
    if (!S.bond) S.bond = { elasticity: null, integrity: 'unknown' };
    S.bond.integrity = {
      springs_back: 'intact',
      stretches_stays: 'overstretched_bond_loss',
      snaps_quick: 'brittle_bond_loss',
      unsure: 'unknown'
    }[S.bond.elasticity] || 'unknown';

    // hydration coating dependency
    if (!S.hydration) S.hydration = {};
    S.hydration.coating_dependent =
      S.hydration.product_response === 'temporary_coat' ||
      S.hydration.product_response === 'weighs_down' ||
      S.hydration.product_response === 'nothing_helps';

    // severity score + band
    var score = severityFromSignals(S, profile.frustration_theme);
    profile.severity_score = score;
    var band = severityBand(score);

    // bond override: structural bond loss forces ≥ moderate_severe
    if ((S.bond.integrity === 'brittle_bond_loss' || S.bond.integrity === 'overstretched_bond_loss') &&
        SEVERITY_ORD[band] < SEVERITY_ORD.moderate_severe) {
      band = 'moderate_severe';
    }
    profile.severity = band;

    // dominant + secondary mechanisms
    var order = ['chemical', 'thermal', 'mechanical', 'hydration'];
    var dominant = null, domOrd = 0;
    order.forEach(function (dim) {
      var o = LOAD_ORD[loads[dim]] || 0;
      if (o > domOrd) { domOrd = o; dominant = dim; }
    });
    profile.dominant_mechanism = domOrd > 0 ? dominant : null;
    profile.secondary_mechanisms = order.filter(function (dim) {
      return dim !== dominant && (LOAD_ORD[loads[dim]] || 0) >= 2;
    }).sort(function (a, b) { return (LOAD_ORD[loads[b]] || 0) - (LOAD_ORD[loads[a]] || 0); });

    // covered dimensions
    var dims = ['context', 'chemical', 'thermal', 'mechanical', 'hydration', 'bond', 'severity', 'loss_type', 'routine'];
    profile.covered_dimensions = dims.filter(function (d) { return coveredDimension(profile, d); });

    // confidence
    var coreCovered = ['chemical', 'thermal', 'bond'].reduce(function (n, d) {
      return n + (coveredDimension(profile, d) ? 1 : 0);
    }, 0);
    var bondDefinite = S.bond.elasticity === 'springs_back' || S.bond.elasticity === 'stretches_stays' || S.bond.elasticity === 'snaps_quick';
    var bondCoveredDefinite = coveredDimension(profile, 'bond') && bondDefinite;
    var contradiction = !!(profile.flags && profile.flags.contradiction);
    var conf = 0.15 + (0.18 * coreCovered) + (bondCoveredDefinite ? 0.15 : 0) - (contradiction ? 0.15 : 0);
    profile.confidence = Math.max(0, Math.min(1, conf));

    profile.turn_count = (profile.asked_ids || []).length;
    profile.updated_at = new Date().toISOString();
    return profile;
  }

  function dimensionOf(id) { return DIMENSION_OF[id] || null; }

  function gateOpen(id, profile) {
    var node = NODES[id];
    if (!node) return false;
    if (typeof node.gate === 'function') {
      try { return !!node.gate((profile && profile.signals) || {}, profile || {}); }
      catch (e) { return false; }
    }
    return true; // 'always' or unspecified
  }

  // selectNext — deterministic info-gain router + finish gate. The CLIENT
  // is the sole flow authority; Haiku's next_question_hint is advisory
  // (a tie-break only). Guarantees: elasticity asked before finish,
  // chemical branch asked when a process is present, multi-signal
  // free-text skips covered dimensions, 4 ≤ Q ≤ 6.
  function selectNext(profile) {
    var S = profile.signals || {};
    var asked = profile.asked_ids || [];
    var conf = profile.confidence || 0;
    var has = function (id) { return asked.indexOf(id) !== -1; };
    var cov = function (dim) { return coveredDimension(profile, dim); };
    var ask = function (id) { return { finish: false, id: id }; };
    var chem = S.chemical || {};

    var finishNow = function () {
      // elasticity is REQUIRED before finishing — this guarantee outranks
      // the question cap in the (unreachable) edge where it was never asked
      if (!has('sev_elasticity') && gateOpen('sev_elasticity', profile)) return ask('sev_elasticity');
      return { finish: true, id: null };
    };

    // (0) hard ceiling — Q never exceeds the max, even if a forced insert
    // is pending. In the real flow the forced inserts below always fire at
    // Q2–Q4, so this only ever bites the unreachable over-cap edge.
    if (asked.length >= CONFIG.maxQuestions) return finishNow();

    // (1) honesty guard — forced
    if (profile.flags && profile.flags.shedding_suspected && !has('sev_loss_type')) return ask('sev_loss_type');

    // (2) gated chemical follow-up the moment it opens
    if ((chem.process === 'bleach_partial' || chem.process === 'bleach_global') && !isKnown(chem.overlap) && !has('chem_bleach_overlap')) return ask('chem_bleach_overlap');
    if (chem.process === 'color_only' && !isKnown(chem.frequency) && !has('chem_color_freq')) return ask('chem_color_freq');
    if (chem.process === 'relaxer_perm_keratin' && !isKnown(chem.recency) && !has('chem_relaxer_recency')) return ask('chem_relaxer_recency');

    // (3) finish gate (deterministic; overrides any LLM 'complete')
    var coreCovered = cov('chemical') && cov('thermal') && cov('bond');
    if (asked.length >= CONFIG.minQuestions && coreCovered && conf >= CONFIG.confidenceTarget) return finishNow();

    // (4) elasticity required before finish — pull in once ≥2 core dims known
    if (!cov('bond') && (cov('chemical') || cov('thermal')) && asked.length >= 3 && !has('sev_elasticity')) return ask('sev_elasticity');

    // (4b) elasticity == unsure → confirm via the surface read
    if (S.bond && S.bond.elasticity === 'unsure' && !has('sev_surface') && gateOpen('sev_surface', profile)) return ask('sev_surface');

    // (5) highest-priority UNCOVERED dimension, gated
    var complaint = profile.frustration_theme;
    var third = (complaint === 'dryness' || complaint === 'wont_hold') ? 'hp_porosity' : 'mech_detangling';
    var order = ['chemical', 'thermal', third, 'sev_elasticity', 'hp_product_response', 'rt_prior_bondrepair', 'rt_aftercare'];
    var candidates = order.filter(function (id) {
      return !cov(dimensionOf(id)) && gateOpen(id, profile) && !has(id);
    });
    if (profile.next_hint && candidates.indexOf(profile.next_hint) !== -1) return ask(profile.next_hint);
    if (candidates.length) return ask(candidates[0]);

    // floor: never finish below the minimum if anything eligible remains
    if (asked.length >= CONFIG.minQuestions) return finishNow();
    var fallback = ['hp_product_response', 'rt_prior_bondrepair', 'rt_aftercare', 'ctx_goal', 'sev_loss_type', 'sev_surface'];
    var fb = fallback.filter(function (id) { return gateOpen(id, profile) && !has(id); });
    if (fb.length) return ask(fb[0]);
    return finishNow();
  }

  // estimatedTotal — advisory "about N questions" estimate, clamped to
  // [askedCount, maxQuestions]. Drives the progress label only.
  function estimatedTotal(profile) {
    var asked = (profile.asked_ids || []).length;
    var S = profile.signals || {};
    var chem = S.chemical || {};
    var need = 0;
    if (!coveredDimension(profile, 'chemical')) need++;
    if (!coveredDimension(profile, 'thermal')) need++;
    if (!coveredDimension(profile, 'bond') && !((profile.asked_ids || []).indexOf('sev_elasticity') !== -1)) need++;
    if ((chem.process === 'bleach_partial' || chem.process === 'bleach_global') && !isKnown(chem.overlap) && (profile.asked_ids || []).indexOf('chem_bleach_overlap') === -1) need++;
    if (chem.process === 'color_only' && !isKnown(chem.frequency) && (profile.asked_ids || []).indexOf('chem_color_freq') === -1) need++;
    if (chem.process === 'relaxer_perm_keratin' && !isKnown(chem.recency) && (profile.asked_ids || []).indexOf('chem_relaxer_recency') === -1) need++;
    if (profile.flags && profile.flags.shedding_suspected && (profile.asked_ids || []).indexOf('sev_loss_type') === -1) need++;

    var coreCovered = coveredDimension(profile, 'chemical') && coveredDimension(profile, 'thermal') && coveredDimension(profile, 'bond');
    var est = asked + need;
    if (coreCovered && (profile.confidence || 0) >= CONFIG.confidenceTarget) est = asked;
    if (est < asked) est = asked;
    if (est > CONFIG.maxQuestions) est = CONFIG.maxQuestions;
    if (est < CONFIG.minQuestions && !coreCovered) est = Math.min(CONFIG.minQuestions, CONFIG.maxQuestions);
    return est;
  }

  // buildPreliminary — instant, zero-network, brand-safe preliminary
  // result the moment the questions end. Qualitative only; maps to the
  // existing [data-preview-*] slots and the results-page prelim block.
  function buildPreliminary(profile) {
    if (!profile) profile = initProfile(null);
    var sev = profile.severity || 'mild';
    var mech = profile.dominant_mechanism || 'chemical';
    var isPrevention = (profile.frustration_theme === 'prevention' && sev === 'mild') || (sev === 'mild' && (profile.severity_score || 0) === 0);

    var entry = isPrevention ? 'none' : ((sev === 'severe' || sev === 'moderate_severe') ? 'full_system' : 'targeted_start');

    var copy;
    if (isPrevention) {
      copy = PRELIMINARY_COPY.prevention;
    } else {
      var band = PRELIMINARY_COPY[sev] || PRELIMINARY_COPY.moderate;
      var base = band._base || PRELIMINARY_COPY.moderate._base;
      var mechOverlay = band[mech] || {};
      copy = {
        headline: base.headline,
        summary: base.summary,
        insight: mechOverlay.insight || base.insight,
        timeline: base.timeline
      };
    }

    return {
      _preliminary: true,
      commerce_allowed: !isPrevention,
      damage_severity: sev,
      dominant_mechanism: profile.dominant_mechanism || null,
      damage_profile_headline: copy.headline,
      damage_profile_summary: copy.summary,
      key_insight: copy.insight,
      entry_point: entry,
      reconstruction_protocol: {
        entry_point: entry,
        expected_timeline: copy.timeline
      }
    };
  }

  // applyLocale — overlay localized theme copy (QuizConfig.bank) onto the
  // structural bank by key. `copy` is a flat map: promptKey/labelKey/
  // microKey → localized string. Missing keys keep the inline defaults.
  function applyLocale(copy) {
    if (!copy || typeof copy !== 'object') return;
    Object.keys(NODES).forEach(function (id) {
      var n = NODES[id];
      if (n.promptKey && copy[n.promptKey]) n.prompt = copy[n.promptKey];
      if (n.microKey && copy[n.microKey]) n.micro = copy[n.microKey];
      (n.chips || []).forEach(function (c) { if (c.labelKey && copy[c.labelKey]) c.label = copy[c.labelKey]; });
    });
  }

  function getNode(id) { return NODES[id] || null; }

  // configure — let the section settings override CONFIG at boot.
  function configure(opts) {
    if (!opts) return CONFIG;
    if (opts.minQuestions != null) CONFIG.minQuestions = opts.minQuestions;
    if (opts.maxQuestions != null) CONFIG.maxQuestions = opts.maxQuestions;
    if (opts.confidenceTarget != null) CONFIG.confidenceTarget = opts.confidenceTarget;
    return CONFIG;
  }

  // ───────────────────────────────────────────────────────────────
  // PUBLIC API
  // ───────────────────────────────────────────────────────────────
  window.AnatomyQuizBank = {
    VERSION: '1.0.0',
    SCHEMA_VERSION: 2,
    NODES: NODES,
    MANIFEST: MANIFEST,
    SIGNAL_TAXONOMY: SIGNAL_TAXONOMY,
    SIGNAL_WEIGHTS: SIGNAL_WEIGHTS,
    PRELIMINARY_COPY: PRELIMINARY_COPY,
    CONFIG: CONFIG,
    configure: configure,
    initProfile: initProfile,
    mergeSignal: mergeSignal,
    applyChip: applyChip,
    scoreProfile: scoreProfile,
    selectNext: selectNext,
    estimatedTotal: estimatedTotal,
    buildPreliminary: buildPreliminary,
    applyLocale: applyLocale,
    getNode: getNode,
    dimensionOf: dimensionOf,
    gateOpen: gateOpen,
    coveredDimension: coveredDimension,
    severityFromSignals: severityFromSignals,
    severityBand: severityBand,
    isKnown: isKnown
  };
})();
